<template>
	<div class="wrap_business_chart">
		<div class="chart_container">
			<canvas :id="chartId"></canvas>
			<div class="customLegend">
				<div v-for="(item, idx) in slicedCustomLabel" :key="idx" class="customLegend_item">
					<div class="colorArea" :style="{ borderRadius: '999px', backgroundColor: backgroundColorData[idx] }"></div>
					<div class="textArea">
						<span>{{ item.main }} {{ item.sub }}</span>
					</div>
				</div>
			</div>
		</div>
		<button v-if="isPartialData" type="button" class="getMoreBtn" @click="getMoreLegend">
			<img src="@/assets/img/common/arrow_down.svg" alt="arrow_down" loading="lazy" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Chart, registerables } from 'chart.js';

import { GovernanceStructure, MainBusiness_KR } from '@/api/report';
import { numberFormatWithDecimal } from '@/utils/function/format';

// -----chart-----
Chart.register(...registerables);

// -----variable-----
const props = defineProps<{
	data: GovernanceStructure[] | MainBusiness_KR[];
}>();

let chartInstance: any;

// -----type-----
type BusinessChartDataType = {
	data: number[];
	tooltip: string[];
	label: string[];
	customLabel: CustomLabelDataType[];
};
type CustomLabelDataType = {
	main: string;
	sub: string;
};

// -----variable-----
const chartId = ref('chart-' + Math.random().toString(36).substring(2, 9));
const chartDataNumber = ref(5);
const businessChartData = ref<BusinessChartDataType>({
	label: [],
	tooltip: [],
	data: [],
	customLabel: [],
});
const backgroundColorData = [
	'#01A9FF',
	'#FFB83F',
	'#FF5946',
	'#DBAEFF',
	'#A9F4D0',
	'#2B8B5D',
	'#F49698',
	'#FEFFD5',
	'#CFD096',
	'#EAEAEA',
	'#3E61FF',
];
const slicedCustomLabel = computed(() => {
	return businessChartData.value.customLabel.slice(0, chartDataNumber.value);
});
const chartData = computed(() => ({
	labels: businessChartData.value.label,
	datasets: [
		{
			data: businessChartData.value.data,
			backgroundColor: backgroundColorData,
			borderColor: 'transparent',
		},
	],
}));

const isPartialData = computed(() => {
	return chartDataNumber.value < businessChartData.value.label.length ? true : false;
});

// -----function-----
const createChart = () => {
	if (chartId.value) {
		chartInstance = new Chart(chartId.value, {
			type: 'doughnut',
			data: chartData.value,
			options: {
				responsive: true,
				color: '#fff',
				plugins: {
					legend: {
						display: false,
						position: 'bottom',
						labels: {
							usePointStyle: true,
							pointStyle: 'rectRounded',
							padding: 16,
						},
						align: 'center',
						onClick: function () {
							console.log('click legend');
						},
					},
					tooltip: {
						callbacks: {
							title: function (tooltipItems) {
								const tooltipIndex = tooltipItems[0].dataIndex; // 첫 번째 항목 인덱스 사용
								return businessChartData.value.label[tooltipIndex]; // label 배열에서 제목으로 사용할 값 반환
							},
							label: function (tooltipItem) {
								const tooltipIndex = tooltipItem.dataIndex;
								return businessChartData.value.tooltip[tooltipIndex];
							},
						},
					},
				},
				cutout: '50%',
				// radius: 150,
			},
		});
	}
	console.log('chartData.value:', chartData.value);
};
const updateChart = () => {
	if (chartInstance) {
		chartInstance.data.labels = businessChartData.value.label.slice(0, chartDataNumber.value);
		chartInstance.data.datasets[0].data = businessChartData.value.data.slice(0, chartDataNumber.value);
		chartInstance.update();
	}
};
const getMoreLegend = () => {
	if (isPartialData.value) {
		chartDataNumber.value += 5;
		updateChart();
	}
};
const formatNumber = (value: string): string => parseFloat(value).toString();

// -----composition-----
onMounted(() => {
	props.data.forEach((item: any) => {
		businessChartData.value.label.push(
			`${item.event_major_business_sector ?? item.event_governance_structure_client} (${formatNumber(item.event_major_business_share ?? item.event_governance_structure_share)}%)`
		);
		businessChartData.value.data.push(item.event_major_business_share ?? item.event_governance_structure_share);
		businessChartData.value.tooltip.push(
			item.event_major_business_sector_description ?? item.event_governance_structure_description
		);

		const isMajorBusiness = item.event_major_business_take !== undefined;

		businessChartData.value.customLabel.push({
			main: isMajorBusiness ? item.event_major_business_sector : item.event_governance_structure_client,
			sub: isMajorBusiness
				? `(매출액: ${numberFormatWithDecimal(item.event_major_business_take)}, 단위: ${item.event_major_business_unit}, 점유율: ${formatNumber(item.event_major_business_share)}%)`
				: `(보유지분: ${numberFormatWithDecimal(item.event_governance_structure_take)}, 단위: ${item.event_governance_structure_unit}, 지분율: ${formatNumber(item.event_governance_structure_share)}%)`,
		});
	});

	createChart();
});

onUnmounted(() => {
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
});
</script>

<style scoped lang="scss">
.wrap_business_chart {
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	.chart_container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2.4rem;
		canvas {
			max-width: 100%;
			width: 100%;
			max-height: 30rem;
			height: auto;
		}
		.customLegend {
			display: flex;
			flex-direction: column;
			gap: 1.6rem;
			.customLegend_item {
				display: flex;
				align-items: center;
				gap: 0.4rem;
				margin: auto;
				@media screen and (max-width: 768px) {
					margin: 0;
				}
				.colorArea {
					width: 3rem;
					height: 1.2rem;
				}
				.textArea {
					display: flex;
					flex-direction: row;
					gap: 0.3rem;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: normal;
					@media screen and (max-width: 768px) {
						flex-direction: row;
						gap: 0;
					}
				}
			}
		}
	}
	.getMoreBtn {
		margin: 0 auto;
		img {
			width: 2.8rem;
			height: 2.8rem;
		}
	}
}
</style>
